import React from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import Button from '../../../components/ui/Button';
import PanelCard from '../../performance-operations/components/PanelCard';
import {
  Badge, ProgressFill, ProgressTrack, Sub,
} from '../../performance-operations/styles';
import { usePanel } from '../../performance-operations/config';
import { fetchTree, formatNumber } from '../config';

// E04 "Hedef Bağlantıları" (analiz §7 + §29): şirket önceliği → birim hedefi → bireysel hedef ağacı. Kurumsal düğüm
// ilerlemesi Kurumsal Hedefler sayfasıyla aynı kuraldan (manuel ya da çocuk + hizalı hedef ortalaması). Bireysel hedef
// sayıları KİŞİ kümesinden — düğüm tıklaması hedef listesini aynı koşulla açar (sayı = liste). Döngüsel bireysel bağlar
// ve iptal (arşiv) kaynağa bağlar geçersiz sayılır (M61); bir hedef iki yoldan bağlı olsa da bir kez sayılır.
const Summary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  margin-bottom: 12px;
`;

const NodeRow = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 1fr) 70px auto;
  gap: 12px;
  align-items: center;
  padding: 8px 10px;
  padding-left: ${({ $depth }) => 10 + $depth * 24}px;
  border-bottom: 1px solid ${({ theme }) => theme.colors.surface.border};
  font-size: ${({ theme }) => theme.metrics.font.size.sm};
  color: ${({ theme }) => theme.colors.text.primary};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    padding-left: 10px;
  }
`;

function Node({ node, depth, onShowGoals }) {
  const { t, i18n } = useTranslation();
  return (
    <>
      <NodeRow $depth={depth} data-node={node.id} role="treeitem" aria-level={depth + 1}>
        <div>
          <div>{node.title}</div>
          <Sub>
            {t(`performance.orgGoals.ownerType.${node.ownerType}`, { defaultValue: node.ownerType })}
            {node.departmentName ? ` · ${node.departmentName}` : ''}
            {node.status === 'archived' ? ` · ${t('performance.goalAnalytics.tree.archived')}` : ''}
          </Sub>
        </div>
        <ProgressTrack><ProgressFill $pct={node.progressPct ?? 0} /></ProgressTrack>
        <div>{node.progressPct != null ? `${formatNumber(node.progressPct, i18n.language)}%` : '-'}</div>
        <div>
          {node.alignedInScope > 0 ? (
            <Button
              variant="ghost"
              onClick={() => onShowGoals({ alignedOrgGoalId: node.id, linked: null })}
              data-action={`node-goals-${node.id}`}
            >
              {t('performance.goalAnalytics.tree.alignedGoals', { count: node.alignedInScope })}
            </Button>
          ) : <Sub>{t('performance.goalAnalytics.tree.noAligned')}</Sub>}
        </div>
      </NodeRow>
      {(node.children || []).map((child) => (
        <Node key={child.id} node={child} depth={depth + 1} onShowGoals={onShowGoals} />
      ))}
    </>
  );
}

export default function GoalTreePanel({ filters, onShowGoals, refreshKey }) {
  const { t } = useTranslation();
  const panel = usePanel(() => fetchTree(filters), [JSON.stringify(filters), refreshKey], !!filters.periodId);
  const d = panel.data;
  const roots = d?.roots || [];

  return (
    <PanelCard
      panelKey="goalTree"
      title={t('performance.goalAnalytics.panel.tree')}
      span={12}
      loading={panel.loading}
      error={panel.error}
      onRetry={panel.retry}
      empty={!!d && roots.length === 0 && d.goalsInScope === 0}
      emptyText={t('performance.goalAnalytics.tree.empty')}
    >
      {d ? (
        <>
          <Summary data-role="tree-summary">
            <Badge>{t('performance.goalAnalytics.tree.goalsInScope', { count: d.goalsInScope })}</Badge>
            <Badge $tone="success">{t('performance.goalAnalytics.tree.linked', { count: d.linkedInScope })}</Badge>
            <Badge $tone={d.unlinkedInScope > 0 ? 'danger' : undefined}>{t('performance.goalAnalytics.tree.unlinked', { count: d.unlinkedInScope })}</Badge>
            {d.cyclicInScope > 0 ? <Badge $tone="danger">{t('performance.goalAnalytics.tree.cyclic', { count: d.cyclicInScope })}</Badge> : null}
            {d.unlinkedInScope > 0 ? (
              <Button variant="ghost" onClick={() => onShowGoals({ linked: false, alignedOrgGoalId: null })} data-action="show-unlinked">
                {t('performance.goalAnalytics.tree.showUnlinked')}
              </Button>
            ) : null}
          </Summary>
          {d.personScope === 'direct' ? <Sub>{t('performance.goalAnalytics.panel.directScopeNote')}</Sub> : null}
          {roots.length === 0 ? <Sub>{t('performance.goalAnalytics.tree.noOrgGoals')}</Sub> : (
            <div role="tree" aria-label={t('performance.goalAnalytics.panel.tree')}>
              {roots.map((n) => <Node key={n.id} node={n} depth={0} onShowGoals={onShowGoals} />)}
            </div>
          )}
          <Sub>{t('performance.goalAnalytics.tree.note')}</Sub>
        </>
      ) : null}
    </PanelCard>
  );
}
